import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import Badge from '../../components/Badge';
import {
  OPTION_STATUS_LABEL,
  MAX_OPTIONS,
  ensurePlatformSession,
  fetchProjectOptions,
} from '../../lib/solutionOptions';

function optionStatusBadge(status) {
  if (status === 'selected') return 'gr';
  if (status === 'assessed') return 'pu';
  if (status === 'in_assessment') return 'am';
  if (status === 'rejected') return 'rd';
  return '';
}

export default function SolutionOptions() {
  const { project, programme } = useOutletContext();
  const navigate = useNavigate();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const [options, setOptions] = useState([]);
  const [baselineLocked, setBaselineLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newOption, setNewOption] = useState({ name: '', description: '' });

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id || !programme?.id) return;
    setLoading(true);
    try {
      const [{ data: proj }, opts] = await Promise.all([
        sb.from('projects').select('requirements_baseline_locked_at').eq('id', project.id).maybeSingle(),
        fetchProjectOptions(project.id, programme.id),
      ]);
      setBaselineLocked(!!proj?.requirements_baseline_locked_at);
      setOptions(opts);
    } catch (e) {
      console.error('[SolutionOptions] load failed', e);
      showToast(e.message || 'Failed to load solution options');
    } finally {
      setLoading(false);
    }
  }, [project?.id, programme?.id]);

  useEffect(() => { load(); }, [load]);

  async function addOption() {
    if (!newOption.name.trim() || !project?.id || !programme?.id) return;
    if (options.length >= MAX_OPTIONS) {
      showToast(`Maximum of ${MAX_OPTIONS} options per project`);
      return;
    }
    setSaving(true);
    try {
      const sessionId = await ensurePlatformSession(programme.id, profile?.id, project.id);
      const { error } = await sb.from('architecture_options').insert({
        session_id: sessionId,
        project_id: project.id,
        name: newOption.name.trim(),
        description: newOption.description.trim() || null,
        status: 'draft',
        order_index: options.length + 1,
      });
      if (error) throw error;
      setNewOption({ name: '', description: '' });
      setShowAdd(false);
      await load();
      showToast('Option created', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to create option');
    } finally {
      setSaving(false);
    }
  }

  async function setOptionStatus(optionId, status) {
    setSaving(true);
    try {
      const { error } = await sb.from('architecture_options').update({ status }).eq('id', optionId);
      if (error) throw error;
      await load();
      showToast('Status updated', 'success');
    } catch (e) {
      showToast(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading solution options…" />;

  const atCap = options.length >= MAX_OPTIONS;
  const selected = options.find((o) => o.status === 'selected');

  return (
    <InitiationLayout
      title="Solution options"
      subtitle={`${project?.name} · Compare up to ${MAX_OPTIONS} approaches`}
      badge={<Badge variant={selected ? 'gr' : 'pu'}>{options.length} / {MAX_OPTIONS}</Badge>}
      actions={
        canWrite ? (
          <>
            <button type="button" className="btn-ghost" onClick={() => navigate(`/project/${project.id}/initiation/bake-off`)}>
              Bake-off comparison
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={saving || atCap || !baselineLocked}
              title={!baselineLocked ? 'Lock requirements baseline first' : atCap ? 'Maximum options reached' : undefined}
              onClick={() => setShowAdd(true)}
            >
              Add option
            </button>
          </>
        ) : null
      }
    >
      {!baselineLocked && (
        <div className="gate-banner warn">
          Requirements baseline must be locked before defining solution options.
          Go to <strong>Requirements</strong> and use <strong>Lock baseline</strong>.
        </div>
      )}

      {atCap && (
        <div className="gate-banner info">
          Maximum of {MAX_OPTIONS} active options reached. Reject an option to add another.
        </div>
      )}

      <div className="so-grid">
        {options.map((opt) => {
          const platforms = Array.isArray(opt.platforms_json) ? opt.platforms_json : [];
          return (
            <div key={opt.id} className={`so-card${opt.status === 'selected' ? ' so-card-winner' : ''}`}>
              <div className="so-card-hdr">
                <div>
                  <div className="so-card-name">{opt.name}</div>
                  {opt.description && <div className="so-card-desc">{opt.description}</div>}
                </div>
                <Badge variant={optionStatusBadge(opt.status)}>
                  {OPTION_STATUS_LABEL[opt.status] || opt.status}
                </Badge>
              </div>
              <div className="so-card-body">
                <div className="so-stat-row">
                  <span>Platforms</span>
                  <span>{platforms.length ? platforms.join(', ') : '—'}</span>
                </div>
                <div className="so-stat-row">
                  <span>Interfaces</span>
                  <span>{opt.interface_count ?? 0}</span>
                </div>
                <div className="so-stat-row">
                  <span>Score</span>
                  <span>{opt.weighted_score != null ? opt.weighted_score : '—'}</span>
                </div>
              </div>
              <div className="so-card-foot">
                {canWrite && opt.status === 'in_assessment' && (
                  <button type="button" className="btn-ghost" disabled={saving} onClick={() => setOptionStatus(opt.id, 'assessed')}>
                    Mark assessed
                  </button>
                )}
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => navigate(`/project/${project.id}/initiation/solution-options/${opt.id}`)}
                >
                  Open workspace
                </button>
              </div>
            </div>
          );
        })}

        {options.length === 0 && baselineLocked && (
          <div className="so-empty card" style={{ padding: 24, gridColumn: '1 / -1' }}>
            <p style={{ color: '#888780', fontSize: 12, marginBottom: 12 }}>
              No solution options yet. Add competing approaches (e.g. wall-to-wall SAP vs SAP + Manhattan).
            </p>
            {canWrite && (
              <button type="button" className="btn-primary" onClick={() => setShowAdd(true)}>Add first option</button>
            )}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)} role="presentation">
          <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="modal-hdr">New solution option</div>
            <div className="form-row">
              <label className="form-label">Name *</label>
              <input
                className="form-input"
                value={newOption.name}
                onChange={(e) => setNewOption((p) => ({ ...p, name: e.target.value }))}
                placeholder='e.g. "Wall-to-wall SAP"'
              />
            </div>
            <div className="form-row">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                rows={3}
                value={newOption.description}
                onChange={(e) => setNewOption((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="modal-foot">
              <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={saving || !newOption.name.trim()} onClick={addOption}>
                {saving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </InitiationLayout>
  );
}
